import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { ProfileCompletionGuard } from '@/app/router/ProfileCompletionGuard';
import { adminPricingRoutes } from '@/features/admin-pricing/routes';
import { authRoutes } from '@/features/auth/routes';
import { useAuthStore } from '@/features/auth/store/authStore';
import { postAuthLanding } from '@/features/auth/utils/postAuthLanding';
import { customizationRoutes } from '@/features/customization/routes';
import { dashboardRoutes } from '@/features/dashboard/routes';
import { questionnairesRoutes } from '@/features/questionnaires/routes';
import { rolesRoutes } from '@/features/roles/routes';
import { PublicSessionRunScreen } from '@/features/sessions/pages/PublicSessionRunScreen';
import { PublicWorkflowEntryScreen } from '@/features/sessions/pages/PublicWorkflowEntryScreen';
import { sessionsRoutes } from '@/features/sessions/routes';
import { settingsRoutes } from '@/features/settings/routes';
import { templatesRoutes } from '@/features/templates/routes';
import { usersRoutes } from '@/features/users/routes';
import { workflowsRoutes } from '@/features/workflows/routes';

// Top-level router. Per-feature route fragments compose here. Root `/` is
// the login page when signed out, and bounces to the auth landing page
// when signed in (so a returning user with a live session doesn't see the
// login form again).
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      {authRoutes}

      {/* Public applicant run — anonymous, OUTSIDE the auth shell. The shared
          link /w/{token} lets an applicant run the workflow with no login. */}
      <Route path="/w/:token" element={<PublicSessionRunScreen />} />

      {/* Shared workflow link — one common URL per workflow. Verifies a mobile
          number by OTP, which creates or resumes that number's run, then
          redirects to /w/{token}. ?ref= optional. */}
      <Route path="/wf/:slug" element={<PublicWorkflowEntryScreen />} />

      {/* ProfileCompletionGuard wraps all app routes once: if a tenant member
          has no firstName+lastName, they're redirected to /settings to complete
          their profile before using the app. /settings itself is excluded from
          the redirect to prevent an infinite loop. */}
      <Route
        element={
          <ProfileCompletionGuard>
            <Outlet />
          </ProfileCompletionGuard>
        }
      >
        {dashboardRoutes}
        {settingsRoutes}
        {customizationRoutes}
        {workflowsRoutes}
        {templatesRoutes}
        {questionnairesRoutes}
        {sessionsRoutes}
        {usersRoutes}
        {rolesRoutes}
        {adminPricingRoutes}
      </Route>
      <Route
        path="*"
        element={
          <div className="flex min-h-dvh items-center justify-center">
            <p className="text-foreground">Route not yet implemented.</p>
          </div>
        }
      />
    </Routes>
  );
}

function RootRedirect() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);

  if (isAuthenticated && user) {
    return <Navigate to={postAuthLanding(user)} replace />;
  }

  // Not authenticated — send to a protected route so ProtectedRoute
  // handles the OAuth redirect to identity.signcare.io.
  return <Navigate to="/dashboard" replace />;
}
